/**
 * About Page
 * Information about the organizations that built the FAAC visualization
 */

import { Footer } from '../components/Footer';

interface AboutPageProps {
  onNavigateBack?: () => void;
}

const ExternalLinkIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
  </svg>
);

export function AboutPage({ onNavigateBack }: AboutPageProps) {
  return (
    <div className="min-h-screen flex flex-col bg-surface-950">
      {/* Header */}
      <header className="glass border-b border-surface-700/50 sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={onNavigateBack}
                className="flex items-center gap-2 text-surface-400 hover:text-accent-400 transition-colors"
              >
                <ArrowLeftIcon />
                <span className="hidden sm:inline">Back</span>
              </button>
              <div className="h-6 w-px bg-surface-700" />
              <a href="#" className="text-xl font-display font-bold text-surface-100 hover:text-accent-400 transition-colors">
                FAAC<span className="text-accent-500">viz</span>
              </a>
            </div>
            <span className="px-3 py-1 bg-surface-800 text-surface-400 text-sm rounded-full">
              About
            </span>
          </div>
        </div>
      </header>
      
      {/* Main content */}
      <main className="flex-1">
        {/* Hero section */}
        <section className="relative py-20 overflow-hidden">
          {/* Background effects */}
          <div className="absolute inset-0 bg-gradient-to-b from-accent-500/5 to-transparent" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-accent-500/10 rounded-full blur-3xl" />
          
          <div className="container mx-auto px-4 relative">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-display font-bold text-surface-100 mb-6">
                About This Project
              </h1>
              <p className="text-lg text-surface-400 leading-relaxed">
                FAAC Viz is an interactive data visualization that makes Nigeria's 
                Federation Account Allocation Committee (FAAC) data accessible, 
                understandable, and explorable for everyone.
              </p>
            </div>
          </div>
        </section>
        
        {/* Mission section */}
        <section className="py-16 bg-surface-900/30">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-display font-bold text-surface-100 mb-6 text-center">
                Our Mission
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center p-6 rounded-xl bg-surface-800/50 border border-surface-700/50">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-lg bg-accent-500/20 flex items-center justify-center">
                    <svg className="w-6 h-6 text-accent-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-surface-100 mb-2">Transparency</h3>
                  <p className="text-sm text-surface-400">
                    Making public finance data visible and accessible to all citizens
                  </p>
                </div>
                
                <div className="text-center p-6 rounded-xl bg-surface-800/50 border border-surface-700/50">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-lg bg-accent-500/20 flex items-center justify-center">
                    <svg className="w-6 h-6 text-accent-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-surface-100 mb-2">Insight</h3>
                  <p className="text-sm text-surface-400">
                    Transforming raw numbers into meaningful visual stories
                  </p>
                </div>
                
                <div className="text-center p-6 rounded-xl bg-surface-800/50 border border-surface-700/50">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-lg bg-accent-500/20 flex items-center justify-center">
                    <svg className="w-6 h-6 text-accent-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-surface-100 mb-2">Accountability</h3>
                  <p className="text-sm text-surface-400">
                    Empowering citizens to understand and track public resource allocation
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Organizations section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-display font-bold text-surface-100 mb-4 text-center">
                Built By
              </h2>
              <p className="text-surface-400 text-center mb-12 max-w-2xl mx-auto">
                This visualization is a collaboration between two organizations committed 
                to using technology and data for positive impact in Nigeria and beyond.
              </p>
              
              <div className="grid md:grid-cols-2 gap-8">
                {/* Dumbledore Solutions */}
                <div className="group relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-accent-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="relative p-8 rounded-2xl bg-surface-900/80 border border-surface-700/50 hover:border-accent-500/30 transition-all h-full">
                    {/* Logo */}
                    <div className="w-20 h-20 rounded-xl bg-surface-800 flex items-center justify-center mb-6 overflow-hidden">
                      <img 
                        src="/dumbledore-logo.svg" 
                        alt="Dumbledore Solutions" 
                        className="w-16 h-16 object-contain"
                      />
                    </div>
                    
                    {/* Content */}
                    <h3 className="text-xl font-display font-bold text-surface-100 mb-3">
                      Dumbledore Solutions
                    </h3>
                    
                    <p className="text-surface-400 text-sm leading-relaxed mb-6">
                      Dumbledore Solutions is a leading software development company specializing 
                      in custom software solutions, AI integration, and workflow automation. 
                      They transform businesses through cutting-edge technology, delivering 
                      innovative solutions with magical precision and excellence.
                    </p>
                    
                    {/* Services */}
                    <div className="mb-6">
                      <h4 className="text-xs font-semibold text-surface-500 uppercase tracking-wider mb-3">
                        Services
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {['Software Development', 'AI Integration', 'Workflow Automation'].map((service) => (
                          <span 
                            key={service}
                            className="px-3 py-1 text-xs bg-purple-500/10 text-purple-300 rounded-full border border-purple-500/20"
                          >
                            {service}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    {/* CTA */}
                    <a 
                      href="https://dumbledoretech.com/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-surface-800 hover:bg-surface-700 text-accent-400 rounded-lg text-sm font-medium transition-colors"
                    >
                      Visit Website
                      <ExternalLinkIcon />
                    </a>
                  </div>
                </div>
                
                {/* Dataphyte */}
                <div className="group relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="relative p-8 rounded-2xl bg-surface-900/80 border border-surface-700/50 hover:border-accent-500/30 transition-all h-full">
                    {/* Logo */}
                    <div className="w-20 h-20 rounded-xl bg-white flex items-center justify-center mb-6 overflow-hidden p-2">
                      <img 
                        src="/dataphyte.png" 
                        alt="Dataphyte" 
                        className="w-full h-full object-contain"
                      />
                    </div>
                    
                    {/* Content */}
                    <h3 className="text-xl font-display font-bold text-surface-100 mb-3">
                      Dataphyte Foundation
                    </h3>
                    
                    <p className="text-surface-400 text-sm leading-relaxed mb-6">
                      Dataphyte believes data is power, possibility, and progress. They make 
                      public data usable by repurposing it into formats that support policy 
                      advocacy, transparency, and accountability. Their approach is built on 
                      three pillars: Access, Insight, and Resourcing.
                    </p>
                    
                    {/* Focus areas */}
                    <div className="mb-6">
                      <h4 className="text-xs font-semibold text-surface-500 uppercase tracking-wider mb-3">
                        Focus Areas
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {['Data Access', 'Policy Insight', 'Civic Tech'].map((area) => (
                          <span 
                            key={area}
                            className="px-3 py-1 text-xs bg-blue-500/10 text-blue-300 rounded-full border border-blue-500/20"
                          >
                            {area}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    {/* CTA */}
                    <a 
                      href="https://dataphyte.org/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-surface-800 hover:bg-surface-700 text-accent-400 rounded-lg text-sm font-medium transition-colors"
                    >
                      Visit Website
                      <ExternalLinkIcon />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Data section */}
        <section className="py-16 bg-surface-900/30">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-display font-bold text-surface-100 mb-6 text-center">
                About the Data
              </h2>
              <div className="p-6 rounded-xl bg-surface-800/50 border border-surface-700/50">
                <p className="text-surface-400 leading-relaxed mb-4">
                  The Federation Account Allocation Committee (FAAC) is responsible for 
                  distributing revenues from the Federation Account to the three tiers of 
                  government in Nigeria: Federal, State, and Local Governments.
                </p>
                <p className="text-surface-400 leading-relaxed mb-4">
                  This visualization presents state-level allocation data spanning from 
                  2007 to 2025, allowing users to explore trends, compare states, and 
                  understand how federal resources are distributed across Nigeria's 
                  36 states and the Federal Capital Territory.
                </p>
                <div className="flex items-center gap-4 pt-4 border-t border-surface-700">
                  <div className="flex-1">
                    <p className="text-xs text-surface-500 mb-1">Time Period</p>
                    <p className="text-surface-300 font-medium">2007 – 2025</p>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-surface-500 mb-1">Coverage</p>
                    <p className="text-surface-300 font-medium">36 States + FCT</p>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-surface-500 mb-1">Source</p>
                    <p className="text-surface-300 font-medium">FAAC Records</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* CTA section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-2xl font-display font-bold text-surface-100 mb-4">
                Ready to Explore?
              </h2>
              <p className="text-surface-400 mb-8">
                Dive into the data and discover insights about Nigeria's federal allocations.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="#"
                  className="px-6 py-3 bg-gradient-to-r from-accent-500 to-cyan-500 text-white rounded-lg font-medium hover:from-accent-400 hover:to-cyan-400 transition-all flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                  Open Explorer
                </a>
                <a
                  href="#story"
                  className="px-6 py-3 bg-surface-800 text-surface-100 rounded-lg font-medium hover:bg-surface-700 transition-colors flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  View Data Story
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      {/* Footer */}
      <Footer minimal />
    </div>
  );
}

export default AboutPage;

